/**
 * Paginated fetch for Supabase / PostgREST queries.
 *
 * PostgREST defaults to a max of 1000 rows per response. When a query needs
 * to read more than that (e.g. all predictions across many leagues on the
 * Dashboard), call this helper instead of a single `.select()` — it pages
 * through `.range(from, to)` until a short page (< pageSize) is returned.
 *
 * `builder` MUST return a fresh query builder on each invocation, because
 * `.range()` mutates and a builder can only be awaited once.
 */
export const fetchAllPaginated = async <T>(
  builder: () => { range: (from: number, to: number) => PromiseLike<{ data: any; error: any }> },
  pageSize = 1000,
): Promise<T[]> => {
  const out: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await builder().range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data as T[] | null) ?? [];
    out.push(...rows);
    if (rows.length < pageSize) break;
  }
  return out;
};
